/**
 * Drift view (Phase 4).
 *
 * Surfaces categorised mismatches between Odoo's product_variants /
 * bom_lines and Onshape's parts / assembly_lines.
 *
 * Internal MUI <Tabs> for the four categories with row-count badges.
 * Per-category sortable + searchable table; reuses the row patterns
 * from TruthTellerTables.tsx so the visual language stays consistent.
 *
 * Empty state: "No drift detected -- Odoo and Onshape agree" (good).
 * Truncation banner shown when the backend caps the bucket at 500 rows.
 */
import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DriftCategory, DriftCategoryKey, DriftReport, DriftRow } from '../../types/manufacturing';

interface Props {
  drift: DriftReport | null;
  loading: boolean;
}

interface CategoryDef {
  key: DriftCategoryKey;
  label: string;
  // Column definitions per category. The DriftRow.detail dict carries
  // category-specific fields; each accessor pulls them out as a string.
  columns: Array<{
    id: string;
    label: string;
    accessor: (row: DriftRow) => string;
  }>;
  emptyHelp: string;
}

const CATEGORIES: CategoryDef[] = [
  {
    key: 'odoo_only',
    label: 'Odoo only',
    columns: [
      { id: 'code', label: 'Code', accessor: (r) => r.code },
      { id: 'name', label: 'Name', accessor: (r) => r.name ?? '' },
    ],
    emptyHelp: 'No Odoo-only variants. Every default_code has a matching Onshape part_number.',
  },
  {
    key: 'onshape_only',
    label: 'Onshape only',
    columns: [
      { id: 'code', label: 'Part number', accessor: (r) => r.code },
      { id: 'name', label: 'Name', accessor: (r) => r.name ?? '' },
      {
        id: 'revision',
        label: 'Revision',
        accessor: (r) => (r.detail.revision as string | null | undefined) ?? '',
      },
    ],
    emptyHelp: 'No Onshape-only parts. Every part_number has a matching Odoo default_code.',
  },
  {
    key: 'revision_mismatch',
    label: 'Revision mismatch',
    columns: [
      { id: 'code', label: 'Code', accessor: (r) => r.code },
      {
        id: 'odoo_revision',
        label: 'Odoo revision',
        accessor: (r) => (r.detail.odoo_revision as string | null | undefined) ?? '',
      },
      {
        id: 'onshape_revision',
        label: 'Onshape revision',
        accessor: (r) => (r.detail.onshape_revision as string | null | undefined) ?? '',
      },
    ],
    emptyHelp:
      'Empty by design today -- Odoo product_variants has no revision column yet. The slot is wired so revision drift will start appearing as soon as Odoo grows the field.',
  },
  {
    key: 'hierarchy_mismatch',
    label: 'Hierarchy mismatch',
    columns: [
      {
        id: 'parent_code',
        label: 'Parent code',
        accessor: (r) => (r.detail.parent_code as string | null | undefined) ?? '',
      },
      { id: 'code', label: 'Child code', accessor: (r) => r.code },
      {
        id: 'kind',
        label: 'Kind',
        accessor: (r) => (r.detail.kind as string | null | undefined) ?? '',
      },
    ],
    emptyHelp:
      'No hierarchy drift. Every BOM whose parent matches an Onshape assembly has the same set of child codes.',
  },
];

type SortDir = 'asc' | 'desc';

function compareStrings(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
}

function CategoryTable({ category, rows }: { category: CategoryDef; rows: DriftRow[] }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState<string>(category.columns[0].id);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      category.columns.some((c) => c.accessor(r).toLowerCase().includes(q))
    );
  }, [rows, search, category]);

  const sorted = useMemo(() => {
    const col = category.columns.find((c) => c.id === sortCol) ?? category.columns[0];
    const cmp = (a: DriftRow, b: DriftRow) => compareStrings(col.accessor(a), col.accessor(b));
    const out = [...filtered].sort(cmp);
    if (sortDir === 'desc') out.reverse();
    return out;
  }, [filtered, sortCol, sortDir, category]);

  const handleSort = (colId: string) => {
    if (sortCol === colId) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(colId);
      setSortDir('asc');
    }
  };

  return (
    <Box>
      <TextField
        size="small"
        fullWidth
        placeholder="Filter rows…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 1 }}
        inputProps={{ 'data-testid': `manufacturing-drift-${category.key}-search` }}
      />
      <TableContainer sx={{ maxHeight: '50vh' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {category.columns.map((c) => (
                <TableCell key={c.id} sortDirection={sortCol === c.id ? sortDir : false}>
                  <TableSortLabel
                    active={sortCol === c.id}
                    direction={sortCol === c.id ? sortDir : 'asc'}
                    onClick={() => handleSort(c.id)}
                  >
                    {c.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row, i) => {
              // Drift rows in the "onshape_only" bucket carry part_id in
              // their detail dict (see _compute_onshape_only). Click a row
              // to deep-link into /manufacturing/part/:partId.
              const partId =
                category.key === 'onshape_only'
                  ? (row.detail.part_id as string | null | undefined) ?? null
                  : null;
              return (
                <TableRow
                  key={`${row.code}-${i}`}
                  hover
                  onClick={
                    partId
                      ? () => navigate(`/manufacturing/part/${encodeURIComponent(partId)}`)
                      : undefined
                  }
                  sx={{ cursor: partId ? 'pointer' : 'default' }}
                  data-testid={partId ? `manufacturing-drift-row-${partId}` : undefined}
                >
                  {category.columns.map((c) => (
                    <TableCell key={c.id} sx={{ fontFamily: 'monospace' }}>
                      {c.accessor(row) || '—'}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function CategoryPanel({ category, bucket }: { category: CategoryDef; bucket: DriftCategory }) {
  if (bucket.rows.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No drift detected — Odoo and Onshape agree.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {category.emptyHelp}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {bucket.truncated && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Showing first 500 rows. Resolve drift in batches and refresh.
        </Alert>
      )}
      <CategoryTable category={category} rows={bucket.rows} />
    </Box>
  );
}

export function DriftView({ drift, loading }: Props) {
  const [tab, setTab] = useState<DriftCategoryKey>('odoo_only');

  // Defensive empty buckets so we can render even before data loads.
  const empty: DriftCategory = { rows: [], truncated: false };
  const buckets: Record<DriftCategoryKey, DriftCategory> = {
    odoo_only: drift?.odoo_only ?? empty,
    onshape_only: drift?.onshape_only ?? empty,
    revision_mismatch: drift?.revision_mismatch ?? empty,
    hierarchy_mismatch: drift?.hierarchy_mismatch ?? empty,
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }} data-testid="manufacturing-drift">
      <Typography variant="h6" sx={{ mb: 1 }}>
        Drift
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Mismatches between Odoo's product variants / BOMs and Onshape's parts / assemblies. Resolve
        in the source systems; this view is read-only.
      </Typography>
      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v as DriftCategoryKey)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {CATEGORIES.map((c) => (
          <Tab
            key={c.key}
            value={c.key}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>{c.label}</span>
                <Chip
                  size="small"
                  label={buckets[c.key].rows.length}
                  color={buckets[c.key].rows.length > 0 ? 'warning' : 'default'}
                />
              </Box>
            }
            data-testid={`manufacturing-drift-tab-${c.key}`}
          />
        ))}
      </Tabs>
      {loading && drift === null && (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Loading drift…
          </Typography>
        </Box>
      )}
      {loading && drift !== null && <LinearProgress sx={{ mb: 1 }} />}
      {(() => {
        const cat = CATEGORIES.find((c) => c.key === tab) ?? CATEGORIES[0];
        return <CategoryPanel category={cat} bucket={buckets[cat.key]} />;
      })()}
    </Paper>
  );
}
